import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { isEmpty, omit } from "lodash";
import { useFormik } from "formik";

import Stepper from "common/Stepper";
import BreadCrumb from "Components/Common/BreadCrumb";
import Backdrop from "Components/Common/Backdrop";
import { getOptionValue, utilityTunnelDepartments } from "common/enums";
import ApplicantDetails from "./ApplicantDetails";
import AccessDetails from "./AccessDetails";
import { getAccessDetailSchema } from "utils/utilityTunnelValidationSchema";
import {
  useLazyGetUtilityTunnelDetailQuery,
  usePostRegisterAccessDetailMutation,
} from "apis/UtilityTunnel/AccessCardDetails";
import moment from "moment";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { utilityTunnelMyRequestApplicationRoute } from "Routes/constants";
import { Typography } from "@mui/material";
import { utilityTunnelConsumerRoles } from "common/constant";
import { isAuthorizedUser } from "utils";

const keysToDeleteForEmployee = [
  "employeeContractorDetail.purposeOfVisit",
  "employeeContractorDetail.companyName",
  "employeeContractorDetail.utaCardIssuanceRole",
  "employeeContractorDetail.workingHoursFrom",
  "employeeContractorDetail.workingHoursTo",
  "extraValues",
];

const keysToDeleteForContractor = [
  "employeeContractorDetail.employeeId",
  "employeeContractorDetail.designation",
  "extraValues",
];

const AccessDetailsApplication = ({
  editApplicationId,
  handleClose = () => {},
  t,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const { user, roles } = useSelector((state) => state.Login);

 const isConsumer = useMemo(
    () => isAuthorizedUser(roles, utilityTunnelConsumerRoles),
    [roles]
  );

  const [
    getApplicationData,
    {
      data: { data: applicationData } = {},
      isLoading: isApplicationDataLoading,
    },
  ] = useLazyGetUtilityTunnelDetailQuery();

  const [registerEmployeeOrContractor, { isLoading: isRegistering }] =
    usePostRegisterAccessDetailMutation();

  const createAccessApplication = async (values) => {
    const isEmployee =
      getOptionValue("utilityTunnelConsumerType", "Employee") ===
      values?.accessDetails?.accessCardHolderType;

    const departmentId = values?.employeeContractorDetail?.departmentId;

    const isCapexRequired = utilityTunnelDepartments.find(
      ({ value }) => value === departmentId
    )?.isCapexOpex;

    const updatedData = omit(
      values,
      isEmployee
        ? keysToDeleteForEmployee
        : [
            ...keysToDeleteForContractor,
            isCapexRequired ? "" : "employeeContractorDetail.roleId",
          ]
    );

    const payload = {
      [isEmployee ? "employeeDetails" : "contractorDetails"]: {
        ...updatedData.employeeContractorDetail,
        mobileNumber:
          updatedData.employeeContractorDetail.mobileNumber?.toString(),
        ...(!isEmployee && {
          workingHoursFrom: moment(
            updatedData.employeeContractorDetail.workingHoursFrom
          ).format(),
          workingHoursTo: moment(
            updatedData.employeeContractorDetail.workingHoursTo
          ).format(),
        }),
      },
      accessDetails: {
        ...updatedData.accessDetails,
        accessRequiredFrom: moment(
          updatedData.accessDetails.accessRequiredFrom
        ).format("YYYY-MM-DD"),
        accessRequiredTo: moment(
          updatedData.accessDetails.accessRequiredTo
        ).format("YYYY-MM-DD"),
        expectedReturnDate: updatedData.accessDetails.expectedReturnDate
          ? moment(updatedData.accessDetails.expectedReturnDate).format(
              "YYYY-MM-DD"
            )
          : null,
      },
    };

    try {
      await registerEmployeeOrContractor(payload);
      handleClose();
      navigate(utilityTunnelMyRequestApplicationRoute);
    } catch (err) {}
  };

  const initialValues = useMemo(
    () => ({
      employeeContractorDetail: {
        id: applicationData?.employeeContractorDetail?.id ?? 0,
        departmentId:
          applicationData?.employeeContractorDetail?.departmentId ?? "",
        firstName: applicationData?.employeeContractorDetail?.firstName ?? "",
        lastName: applicationData?.employeeContractorDetail?.lastName ?? "",
        email: applicationData?.employeeContractorDetail?.email ?? "",
        mobileNumber:
          applicationData?.employeeContractorDetail?.mobileNumber ?? "",
        employeeId: applicationData?.employeeContractorDetail?.employeeId ?? "",
        designation:
          applicationData?.employeeContractorDetail?.designation ?? "",
        roleId:
          applicationData?.employeeContractorDetail?.utaCardIssuanceRole?.id ??
          "",
        companyName:
          applicationData?.employeeContractorDetail?.companyName ?? "",
        subContractor:
          applicationData?.employeeContractorDetail?.subContractor ?? "",
        purposeOfVisit:
          applicationData?.employeeContractorDetail?.purposeOfVisit ?? "",
        workingHoursFrom: applicationData?.employeeContractorDetail
          ?.workingHoursFrom
          ? moment(applicationData.employeeContractorDetail.workingHoursFrom)
          : "",
        workingHoursTo: applicationData?.employeeContractorDetail
          ?.workingHoursTo
          ? moment(applicationData.employeeContractorDetail.workingHoursTo)
          : "",
      },
      accessDetails: {
        id: applicationData?.id ?? 0,
        locationId: applicationData?.utaCardIssuanceLocation?.id ?? "",
        doorId: applicationData?.door?.id ?? "",
        accessRequiredFrom: applicationData?.accessRequiredFrom
          ? convertUTCDateToLocalDate(applicationData.accessRequiredFrom)
          : "",
        accessRequiredTo: applicationData?.accessRequiredTo
          ? convertUTCDateToLocalDate(applicationData?.accessRequiredTo)
          : "",
        expectedReturnDate: applicationData?.expectedReturnDate
          ? convertUTCDateToLocalDate(applicationData?.expectedReturnDate)
          : "",
        isAcknowledgment: applicationData?.isAcknowledgment ?? false,
        accessCardHolderType: applicationData?.accessCardHolderType ?? "",
      },
      extraValues: {
        blockId: applicationData?.block?.id ?? "",
        isRegisteringPersonSame: true,
      },
    }),
    [applicationData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: getAccessDetailSchema[currentStep],
    onSubmit: async (values, actions) => {
      if (currentStep === steps.length - 1) {
        createAccessApplication(values);
      } else {
        setCurrentStep((prev) => prev + 1);
        actions.setTouched({});
      }
    },
  });

  const {
    handleSubmit,
    setFieldValue,
    setTouched,
    values: { extraValues: { isRegisteringPersonSame } = {} } = {},
  } = formik;

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: t("Applicant Details"),
        isCompleted: currentStep >= 1,
        component: (
          <ApplicantDetails formik={formik} isEditable={!!editApplicationId} />
        ),
      },
      {
        id: 6,
        name: t("Access Details"),
        isCompleted: currentStep === 1,
        component: <AccessDetails formik={formik} />,
      },
    ],
    [currentStep, formik, applicationData]
  );

  const getApplicationDataByApplicationId = async (editApplicationId) => {
    try {
      await getApplicationData(editApplicationId);
    } catch (error) {}
  };

  useEffect(() => {
    if (editApplicationId) {
      getApplicationDataByApplicationId(editApplicationId);
    }
  }, [editApplicationId]);

  const onAuthorizedPersonChange = async () => {
    if (isRegisteringPersonSame) {
      setTouched({});
      const isEmployee = roles.includes("Employee");
      const holderType = getOptionValue(
        "utilityTunnelConsumerType",
        isEmployee ? "Employee" : "Contractor"
      );

      setFieldValue("employeeContractorDetail.email", user?.email ?? "");
      setFieldValue(
        "employeeContractorDetail.firstName",
        user?.firstName ?? ""
      );
      setFieldValue("employeeContractorDetail.lastName", user?.lastName ?? "");
      setFieldValue(
        "employeeContractorDetail.mobileNumber",
        user?.mobileNumber ?? ""
      );
      setFieldValue("accessDetails.accessCardHolderType", holderType);
    } else {
      setTouched({});
      setFieldValue("employeeContractorDetail.email", "");
      setFieldValue("employeeContractorDetail.firstName", "");
      setFieldValue("employeeContractorDetail.lastName", "");
      setFieldValue("employeeContractorDetail.mobileNumber", "");
      setFieldValue("accessDetails.accessCardHolderType", "");
    }
  };

  useEffect(() => {
    if (isEmpty(applicationData)) {
      onAuthorizedPersonChange();
    }
  }, [isRegisteringPersonSame, user]);

  return (
    <div className={!editApplicationId ? "h-100" : ""}>
      <Backdrop open={isApplicationDataLoading} />

      {(!user?.signatureImage || !user?.profileImage) && isConsumer && (
        <Typography color={"error"}>
          * Please upload your profile image and signature to proceed with application.
        </Typography>
      )}

      {!editApplicationId && (
        <BreadCrumb title={t("Access Application Form")} pageTitle={t("Access Details")} />
      )}
      <div className="stepper-container">
        <Stepper
          steps={steps}
          handleSubmit={handleSubmit}
          activeStepIndex={currentStep}
          setActiveStepIndex={setCurrentStep}
          isLoading={isRegistering}
          formik={formik}
          isDraftDisabled
        />
      </div>
    </div>
  );
};

AccessDetailsApplication.propTypes = {
  editApplicationId: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(AccessDetailsApplication);
